import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink, Loader2, Lock, Search, X } from "lucide-react";
import api from "../../lib/api";
import { LINK_KINDS, STATUS_LABELS, STATUS_TONES, decisionChips, linkKindLabel, linkTarget } from "../../lib/governance";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../ui/responsive-popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function Field({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <dt className="text-xs sm:text-sm text-slate-500 sm:w-36 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

const TONES = {
  slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-50 text-emerald-700", sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-800", red: "bg-red-50 text-red-700",
};
export function Chip({ label, tone = "slate", testid, children }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${TONES[tone] || TONES.slate}`} data-testid={testid}>{children}{label}</span>;
}

// The state of a decision and, when it applies, « non tranchée » / « à revoir » / « remplacée » (computed by the server).
export function DecisionState({ decision, testid = "decision-state" }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Chip label={STATUS_LABELS[decision.status] || decision.status} tone={STATUS_TONES[decision.status]} testid={testid} />
      {decisionChips(decision).map((c) => <Chip key={c.id} label={c.label} tone={c.tone} testid={`decision-chip-${c.id}`} />)}
      {decision.restricted && <Chip label="Restreinte" tone="slate" testid="decision-restricted"><Lock size={11} aria-hidden="true" /></Chip>}
    </span>
  );
}

// Active accounts (id and name only, the server's own list) to name as deciders.
export function useGovernancePeople() {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/governance/people").then(({ data }) => { if (alive) setPeople(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return people;
}

export function PeopleMultiSelect({ value, onChange, people, placeholder = "Choisir des personnes", testid }) {
  const names = value.map((id) => people.find((p) => p.id === id)?.name || "…");
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal h-10" data-testid={testid} aria-label={placeholder}>
          <span className="truncate text-left text-slate-700">{value.length ? names.join(", ") : placeholder}</span>
          <ChevronDown size={14} className="ml-2 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {people.length === 0 && <p className="px-2 py-3 text-sm text-slate-500">Aucune personne à proposer.</p>}
          {people.map((p) => (
            <label key={p.id} className="flex items-center gap-2 rounded px-2 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`${testid}-option-${p.id}`}>
              <Checkbox checked={value.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
              <span className="truncate">{p.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// People a decision concerns: accounts, or a name typed for someone outside the CRM (nothing else is asked about them).
export function InterestedField({ value, onChange, people }) {
  const [name, setName] = useState("");
  const add = (person) => {
    const key = (p) => (p.type === "external" ? `x:${p.name.toLowerCase()}` : `${p.type}:${p.id}`);
    if (!value.some((v) => key(v) === key(person))) onChange([...value, person]);
  };
  const addExternal = () => {
    const clean = name.trim();
    if (clean.length >= 2) add({ type: "external", name: clean });
    setName("");
  };
  const accountIds = value.filter((p) => p.type === "account").map((p) => p.id);
  return (
    <div className="space-y-2" data-testid="interested-field">
      <PeopleMultiSelect value={accountIds} people={people} placeholder="Choisir des comptes" testid="interested-accounts"
        onChange={(ids) => onChange([...value.filter((p) => p.type !== "account"), ...ids.map((id) => ({ type: "account", id, name: people.find((p) => p.id === id)?.name }))])} />
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ou le nom d'une personne hors du CRM" className="h-10"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExternal(); } }} aria-label="Nom d'une personne hors du CRM" data-testid="interested-name" />
        <Button type="button" variant="outline" onClick={addExternal} disabled={name.trim().length < 2} data-testid="interested-add">Ajouter</Button>
      </div>
      {value.some((p) => p.type === "external") && (
        <ul className="flex flex-wrap gap-1.5">
          {value.filter((p) => p.type === "external").map((p) => (
            <li key={p.name}><Chip label={p.name} tone="slate"><button type="button" onClick={() => onChange(value.filter((v) => v !== p))} aria-label={`Retirer ${p.name}`}><X size={11} /></button></Chip></li>
          ))}
        </ul>
      )}
    </div>
  );
}

// What a decision points at. Each module answers with its own rule, so the list only offers what the person can already see.
export function LinkPicker({ value, onChange }) {
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    if (!kind) { setOptions([]); return undefined; }
    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/governance/link-options", { params: { kind, q } })
        .then(({ data }) => { if (mine === seq.current) setOptions(data.items || []); })
        .catch(() => { if (mine === seq.current) setOptions([]); })
        .finally(() => { if (mine === seq.current) setBusy(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [kind, q]);
  const has = (o) => value.some((l) => l.kind === kind && l.ref_id === o.ref_id);
  return (
    <div className="space-y-2" data-testid="link-picker">
      <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <Select value={kind || undefined} onValueChange={(k) => { setKind(k); setQ(""); }}>
          <SelectTrigger className="h-10" aria-label="Type d'élément à lier" data-testid="link-kind"><SelectValue placeholder="Type d'élément" /></SelectTrigger>
          <SelectContent>{LINK_KINDS.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 h-10" disabled={!kind} aria-label="Rechercher l'élément à lier" data-testid="link-search" />
        </div>
      </div>
      {kind && (
        <ul className="rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="link-options">
          {busy && <li className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Recherche…</li>}
          {!busy && options.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">Rien à proposer{q ? " pour cette recherche" : ""}.</li>}
          {!busy && options.map((o) => (
            <li key={o.ref_id}>
              <button type="button" disabled={has(o)} onClick={() => onChange([...value, { kind, ref_id: o.ref_id, visible: true, label: o.label }])}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50" data-testid={`link-option-${o.ref_id}`}>
                <span className="truncate">{o.label}</span>{has(o) && <span className="text-xs text-slate-500 shrink-0">Déjà lié</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The links of a decision: a label for what the reader can see, a plain « élément non visible » for what they cannot.
export function LinkList({ links, onRemove, openable = true }) {
  if (!links.length) return null;
  return (
    <ul className="space-y-1.5" data-testid="link-list">
      {links.map((l) => {
        const target = linkTarget(l.kind, l.ref_id);
        return (
          <li key={`${l.kind}:${l.ref_id}`} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`link-${l.kind}-${l.ref_id}`}>
            <span className="min-w-0">
              <span className="text-xs text-slate-500 mr-2">{linkKindLabel(l.kind)}</span>
              {l.visible === false ? <span className="text-slate-500 italic">Élément non visible avec vos droits</span>
                : openable ? <Link to={target.to} state={target.state} className="text-jamora hover:underline inline-flex items-center gap-1">{l.label}<ExternalLink size={12} aria-hidden="true" /></Link>
                  : <span className="text-slate-900">{l.label}</span>}
            </span>
            {onRemove && <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => onRemove(l)} aria-label="Retirer ce lien" data-testid={`link-remove-${l.ref_id}`}><X size={14} /></Button>}
          </li>
        );
      })}
    </ul>
  );
}
